import { useState, useEffect } from 'react';
import { GET } from 'services/api';
import { ReadObject } from 'services/storage';
import { CheckPlan } from 'services/plans';

// Cache global para evitar múltiplas chamadas API
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos em millisegundos
let cachedResult = null;
let cacheTimestamp = null;

// Função global para invalidar cache (usada após renovações)
export const invalidateSubscriptionCache = () => {
    cachedResult = null;
    cacheTimestamp = null;
};

const useSubscriptionCheck = () => {
    const [hasActiveSubscription, setHasActiveSubscription] = useState(null); // null = carregando, true = ativo, false = inativo
    const [subscriptionData, setSubscriptionData] = useState(null);
    const [loading, setLoading] = useState(true);
    // Sem dependência de CoreContext aqui: validação deve vir da API

    const runCheck = async (forceRefresh = false) => {
        try {
            setLoading(true);
            
            // Verifica se tem cache válido e não está forçando refresh
            const now = Date.now();
            if (!forceRefresh && cachedResult && cacheTimestamp && (now - cacheTimestamp < CACHE_DURATION)) {
                setHasActiveSubscription(cachedResult.isActive);
                setSubscriptionData(cachedResult.data);
                setLoading(false);
                return;
            }

            const result = await CheckPlan();

            if (!result) {
                setHasActiveSubscription(false);
                setSubscriptionData(null);
                return;
            }

            // LÓGICA ORIGINAL + SUPORTE A GIFTCARD
            const hasRecurrency = result.recurrency;
            const isCanceled = result?.subscription?.canceled;

            // Detectar giftcard apenas pela resposta da API
            let isGiftCard =
                result.subscription_type === 'giftcard' ||
                result.subscription?.subscription_type === 'giftcard' ||
                result.type === 'giftcard' ||
                result.giftcard === true ||
                result.gift_card === true;

            // Extrair data de expiração de possíveis campos padrão
            let exp = result.expiration_date ||
                        result.expire_at ||
                        result.expires_at ||
                        result.giftcard_expiration ||
                        result.subscription?.expiration_date;
            let notExpired = exp ? (new Date(exp) > new Date()) : false; // sem data -> não libera

            // Plano ativo:
            // - recorrente: recurrency e não cancelado
            // - giftcard: explicitamente giftcard E com data válida (outras chaves opcionais podem ser adicionadas depois)
            let isActive = (hasRecurrency && !isCanceled) || (isGiftCard && notExpired && !isCanceled);

            // Se ainda não ativo, tentar buscar o registro de gift-card do usuário (Strapi)
            if (!isActive) {
                try {
                    const storedUser = ReadObject('user');
                    const userId = storedUser?.id;
                    if (userId) {
                        const qs = `/gift-cards?filters[user_id][$eq]=${userId}&sort[0]=date_end:desc&pagination[limit]=1`;
                        const gc = await GET(qs, true);
                        const first = gc?.data?.[0]?.attributes || null;
                        const gcUsed = first?.used;
                        const gcEnd = first?.date_end;
                        const gcStart = first?.date_start;
                        if (gcEnd) {
                            exp = gcEnd;
                            notExpired = new Date(gcEnd) > new Date();
                        }
                        // Ativo se usado pelo usuário e dentro do período
                        const giftcardActive = (gcUsed === true) && notExpired;
                        if (giftcardActive) {
                            isGiftCard = true;
                            isActive = true;
                        }
                    }
                } catch (e) {
                    // Silencia erro de gift-card em produção
                }
            }

            setHasActiveSubscription(isActive);
            setSubscriptionData({ ...result });

            // Salva no cache
            cachedResult = {
                isActive,
                data: { ...result }
            };
            cacheTimestamp = now;
        } catch (error) {
            console.error('Erro ao verificar assinatura:', error);
            setHasActiveSubscription(false);
            setSubscriptionData(null);
            // Limpa cache em caso de erro
            cachedResult = null;
            cacheTimestamp = null;
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        runCheck();
    }, []);
    
    return {
        hasActiveSubscription,
        subscriptionData,
        loading,
        // Função para revalidar manualmente (com opção de forçar refresh)
        revalidate: async (forceRefresh = false) => { 
            await runCheck(forceRefresh); 
        }
    };
};

export default useSubscriptionCheck;
